import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertCircle } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: unknown;
  errorInfo: ErrorInfo | null;
}

/**
 * Error boundary for /profile routes that displays structured error responses
 * from edge functions and RPCs with proper error codes and hints.
 * 
 * Handles errors with structure: { code, message, hint, reason }
 */
export class ProfileErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: unknown) {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, errorInfo: ErrorInfo) {
    console.error('[ProfileErrorBoundary] Error caught:', error, errorInfo);
    this.setState({ error, errorInfo });
  }

  render() {
    if (this.state.hasError) {
      const error = this.state.error as Record<string, unknown> | null;
      const errorData = (error?.data ?? {}) as Record<string, unknown>;
      const code = error?.code || errorData?.code || error?.status;
      const message = error?.message || errorData?.message || error?.reason || 'An error occurred';
      const hint = error?.hint || errorData?.hint;

      return (
        <div className="container mx-auto px-6 py-8">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Profile Error {code && `(${String(code)})`}</AlertTitle>
            <AlertDescription>
              <p>{String(message)}</p>
              {hint && <p className="mt-2 text-sm opacity-80">{String(hint)}</p>}
            </AlertDescription>
          </Alert>
          <div className="mt-4 space-x-2">
            <Button 
              onClick={() => window.location.reload()} 
              variant="default"
            >
              Reload Page
            </Button>
            <Button 
              onClick={() => window.history.back()}
              variant="outline"
            >
              Go Back
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
